import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

// Impersonation lets a system admin make API requests as if they were another
// user. The state lives in localStorage so the axios interceptor (which runs
// outside React) can read it; this provider mirrors it into React state and
// broadcasts changes across tabs via the storage event.
//
// The backend gates impersonation on the REAL token's user being a
// system_admin (see Authentication concern). We don't trust this client-side
// state for anything but UI affordances.

export interface ImpersonationTarget {
  userId: number;
  email: string;
  fullName: string;
}

interface Ctx {
  target: ImpersonationTarget | null;
  start: (target: ImpersonationTarget) => void;
  stop: () => void;
}

const STORAGE_KEY = 'wz.impersonate';
const ImpersonationContext = createContext<Ctx | null>(null);

function readStored(): ImpersonationTarget | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ImpersonationTarget) : null;
  } catch {
    return null;
  }
}

export function readImpersonationFromStorage(): ImpersonationTarget | null {
  return readStored();
}

export function ImpersonationProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<ImpersonationTarget | null>(() => readStored());

  // Multi-tab: if the admin exits impersonation in one tab, the other tabs
  // should see it too. `storage` only fires in OTHER tabs.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setTarget(readStored());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const start = useCallback((next: ImpersonationTarget) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setTarget(next);
    // Blow away cached "current user" data so the UI repaints as the target.
    queryClient.invalidateQueries();
  }, [queryClient]);

  const stop = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setTarget(null);
    queryClient.invalidateQueries();
  }, [queryClient]);

  const value = useMemo(() => ({ target, start, stop }), [target, start, stop]);

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation(): Ctx {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) throw new Error('useImpersonation must be used inside ImpersonationProvider');
  return ctx;
}

// Opinionated entry point for admin "Act as <user>" triggers: starts the
// impersonation and navigates to the dashboard so the admin sees the target's
// perspective immediately. The low-level start()/stop() stay available for
// callers that need to compose differently (e.g. the banner's Exit button).
export function useActAsUser(): (target: ImpersonationTarget) => void {
  const { start } = useImpersonation();
  const navigate = useNavigate();
  return useCallback((target) => {
    start(target);
    navigate('/dashboard');
  }, [start, navigate]);
}
